// frontend/src/stores/auth.js
import { defineStore } from 'pinia';
import AuthService, { clearAllSessionData } from '@/services/AuthService';
import router from '@/router';
import { setCsrfTokenHeader } from '@/services/api'; // Import for explicit clearing on logout

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    company: null,
    isLoggedIn: false,
    loading: false,
    error: null,
    defaultCompany: "Bagusphere",
  }),

  getters: {
    currentUser: (state) => state.user,
    isAuthenticated: (state) => state.isLoggedIn,
    isAdmin: (state) => state.user?.roles?.includes('admin') || false,
    isEditor: (state) => state.user?.roles?.includes('editor') || false,
    isCompanyAdmin: (state) => state.user?.roles?.includes('company_admin') || false,
    isViewer: (state) => state.user?.roles?.includes('viewer') || false,
    canEdit: (state) => state.user?.roles?.includes('admin') || state.user?.roles?.includes('editor') || false,
    defaultCompany: (state) => state.defaultCompany,
  },

  actions: {
    // Save company data to localStorage
    saveCompanyToStorage(companyData) {
      try {
        if (companyData) {
          localStorage.setItem('company_data', JSON.stringify(companyData));
        } else {
          localStorage.removeItem('company_data');
        }
      } catch (error) {
        console.warn('Could not save company data to localStorage:', error);
      }
    },

    // Load company data from localStorage
    loadCompanyFromStorage() {
      try {
        const companyData = localStorage.getItem('company_data');
        if (companyData) {
          return JSON.parse(companyData);
        }
      } catch (error) {
        console.warn('Could not load company data from localStorage:', error);
        localStorage.removeItem('company_data'); // Clean up corrupted data
      }
      return null;
    },

    // Set company data and persist it
    setCompany(companyData) {
      this.company = companyData;
      this.saveCompanyToStorage(companyData);
    },

    async login(credentials) {
      this.loading = true;
      this.error = null;
      try {
        const response = await AuthService.login(credentials); // AuthService now handles CSRF
        this.user = response.data.user;
        this.isLoggedIn = true;
        
        // Fetch and persist company data
        const companyData = await AuthService.getCompanySettings(this.user.company_id);
        this.setCompany(companyData);
        console.log('this.company:', this.company);
        
        // Clear logout flag when user logs in
        sessionStorage.removeItem('user_logged_out');
        
        // Redirect based on original destination or to home
        const redirectPath = router.currentRoute.value.query.redirect || { name: 'Home' };
        router.push(redirectPath);
        return true;
      } catch (err) {
        this.error = err.response?.data?.error || 'Login failed. Please check your credentials.';
        this.isLoggedIn = false;
        this.user = null;
        this.setCompany(null); // Clear company data
        setCsrfTokenHeader(null); // Ensure CSRF is cleared on login failure
        console.error("Login error:", err);
        return false;
      } finally {
        this.loading = false;
      }
    },

    async register(userData) {
        this.loading = true;
        this.error = null;
        try {
            const response = await AuthService.register(userData); // AuthService handles CSRF
            this.user = response.data.user;
            this.isLoggedIn = true; 
            
            // Fetch and persist company data
            const companyData = await AuthService.getCompanySettings(this.user.company_id);
            this.setCompany(companyData);
            console.log('this.company:', this.company);
            
            // Clear logout flag when user registers
            sessionStorage.removeItem('user_logged_out');
            
            router.push({ name: 'Home' });
            return true;
        } catch (err) {
            this.error = err.response?.data?.error || 'Registration failed.';
            this.setCompany(null); // Clear company data
            setCsrfTokenHeader(null);
            console.error("Registration error:", err);
            return false;
        } finally {
            this.loading = false;
        }
    },

    async registerUserAsAdmin(userData) {
      this.loading = true;
      this.error = null;
      try {
          const data = await AuthService.registerAsAdmin(userData);
          return data;
      } catch (err) {
          this.error = err.response?.data?.error || 'Registration failed.';
          console.error("Registration error:", err);
          return err;
      } finally {
          this.loading = false;
      }
  },

    async logout() {
      // Set loading to prevent any other auth operations during logout
      this.loading = true;
      
      try {
        await AuthService.logout(); // AuthService clears CSRF token
      } catch (err) {
        console.error("Logout API error (will proceed with client-side logout):", err);
        this.error = "Logout failed on server, but session cleared locally.";
      } finally {
        // Force clear all authentication state
        this.user = null;
        this.isLoggedIn = false;
        this.error = null;
        this.setCompany(null); // Clear company data from store and storage
        
        // Use comprehensive session clearing
        clearAllSessionData();
        
        // Set a flag to indicate user has logged out (prevents re-auth)
        sessionStorage.setItem('user_logged_out', 'true');
        
        // Force router navigation to login page
        router.push({ name: 'Login' });
        
        // Reset loading state
        this.loading = false;
      }
    },

    async fetchUserStatus() {
      // Check if user has logged out
      const hasLoggedOut = sessionStorage.getItem('user_logged_out');
      if (hasLoggedOut) {
        console.log('User has logged out, skipping status check');
        this.user = null;
        this.isLoggedIn = false;
        this.setCompany(null);
        return false;
      }

      if (this.isLoggedIn && this.user) return; // Avoid redundant checks if already logged in

      this.loading = true;
      this.error = null;
      try {
        const response = await AuthService.getStatus(); // AuthService handles CSRF
        
        // Additional check: if we get a user but the logout flag is set, clear everything
        if (response.data.user && sessionStorage.getItem('user_logged_out') === 'true') {
          console.log('User data received but logout flag is set, clearing session');
          this.user = null;
          this.isLoggedIn = false;
          this.setCompany(null);
          setCsrfTokenHeader(null);
          return false;
        }
        
        this.user = response.data.user;
        this.isLoggedIn = true;
        setCsrfTokenHeader(response.data.csrf_token);
        
        // Load company data from storage if available, or fetch if needed
        if (!this.company && this.user?.company_id) {
          const companyData = this.loadCompanyFromStorage();
          if (companyData) {
            this.company = companyData;
          } else {
            // Fetch fresh company data if not in storage
            try {
              const freshCompanyData = await AuthService.getCompanySettings(this.user.company_id);
              this.setCompany(freshCompanyData);
            } catch (error) {
              console.warn('Could not fetch company data during status check:', error);
            }
          }
        }
        
        return true; // Indicate success
      } catch (err) {
        this.user = null;
        this.isLoggedIn = false;
        this.setCompany(null);
        setCsrfTokenHeader(null); // Clear CSRF if status check fails
        //console.log("User status check failed or no active session:", err.response?.status);
        return false; // Indicate failure
      } finally {
        this.loading = false;
      }
    },
    
    loginWithGoogle() {
      // Redirect the user to your backend's Google login route.
      // The backend will then redirect to Google's authentication page.
      // Use your backend's actual URL.
      window.location.href = 'https://bagusphere.com/rentapi/api/auth/google/login';
    },

    // Initialize store from persistent storage
    initializeFromStorage() {
      try {
        // Load company data from localStorage
        const companyData = this.loadCompanyFromStorage();
        if (companyData) {
          this.company = companyData;
        }
      } catch (error) {
        console.warn('Could not initialize company data from storage:', error);
      }
    },

    clearError() {
        this.error = null;
    }
  },
});
